// src/utils/mcqParser.js

/**
 * Returns an array of uppercase option letters for a given count.
 * e.g. 4 -> ['A', 'B', 'C', 'D']
 */
export function getOptionLetters(count = 4) {
  const letters = [];
  const safeCount = Math.max(2, Math.min(count, 26));
  for (let i = 0; i < safeCount; i++) {
    letters.push(String.fromCharCode(65 + i));
  }
  return letters;
}

/**
 * Serializes questions to the MCQ text format.
 * Format for each question:
 *   "{qNum}) {options} [{notes}]"
 *
 * Rules:
 * - If options exist and notes exist: "1) A,B [Notes....]"
 * - If only options exist: "1) A,B" or "1) A"
 * - If only notes exist: "1) [Notes....]"
 * - If neither: "1)"
 * - Options are always sorted alphabetically (e.g. A,B,C)
 */
export function serializeMCQ(questions) {
  if (!Array.isArray(questions)) return "";

  return questions
    .map((q) => {
      const qNum = q.number;
      const sortedOptions = (q.options || [])
        .slice()
        .sort((a, b) => a.localeCompare(b))
        .join(",");
      const note = (q.note || "").trim();

      let line = `${qNum})`;
      if (sortedOptions && note) {
        line += ` ${sortedOptions} [${note}]`;
      } else if (sortedOptions) {
        line += ` ${sortedOptions}`;
      } else if (note) {
        line += ` [${note}]`;
      }
      return line;
    })
    .join("\n");
}

/**
 * Parses raw MCQ text content from the server into questions and settings.
 * Strictly verifies each non-empty line against:
 *   ^(\d+)\s*\)(?:\s+([A-Za-z](?:\s*,\s*[A-Za-z])*))?(?:\s*\[(.*)\])?\s*$
 *
 * Returns:
 * {
 *   success: boolean,
 *   data?: {
 *     offset: number,
 *     numQuestions: number,
 *     numOptions: number,
 *     questions: Array<{ number: number, options: string[], note: string }>
 *   },
 *   error?: string
 * }
 */
export function parseMCQ(text) {
  if (!text || typeof text !== "string") {
    return { success: false, error: "Empty or invalid file content." };
  }

  const rawLines = text.split(/\r?\n/);
  const lines = rawLines.map((l) => l.trim()).filter((l) => l.length > 0);

  if (lines.length === 0) {
    return { success: false, error: "File contains no question lines." };
  }

  // Regex strictly matching:
  // e.g. "1) A,B [note]" or "1) A" or "1) [note]" or "1)"
  const lineRegex = /^(\d+)\s*\)(?:\s+([A-Za-z](?:\s*,\s*[A-Za-z])*))?(?:\s*\[(.*)\])?\s*$/;

  const parsedQuestions = [];
  let minQ = Infinity;
  let maxQ = -Infinity;
  let maxOptionCode = 68; // Minimum 4 options: 'D' (ASCII 68)

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(lineRegex);
    if (!match) {
      return {
        success: false,
        error: `Syntax error on line ${i + 1}: "${line}". Expected format: "<number>) <options> [<notes>]"`,
      };
    }

    const qNum = parseInt(match[1], 10);
    const optionsRaw = match[2];
    const noteRaw = match[3];

    let options = [];
    if (optionsRaw) {
      options = optionsRaw
        .split(",")
        .map((opt) => opt.trim().toUpperCase())
        .filter(Boolean);

      for (const opt of options) {
        const code = opt.charCodeAt(0);
        if (code >= 65 && code <= 90 && code > maxOptionCode) {
          maxOptionCode = code;
        }
      }
    }

    const note = noteRaw ? noteRaw.trim() : "";

    minQ = Math.min(minQ, qNum);
    maxQ = Math.max(maxQ, qNum);

    parsedQuestions.push({
      number: qNum,
      options: Array.from(new Set(options)).sort((a, b) => a.localeCompare(b)),
      note,
    });
  }

  // Offset represents the last question already set before the first question
  const offset = Math.max(0, minQ - 1);
  const numQuestions = parsedQuestions.length;
  // Calculate options count from highest seen option letter (minimum 4: A-D)
  const numOptions = Math.max(4, maxOptionCode - 65 + 1);

  return {
    success: true,
    data: {
      offset,
      numQuestions,
      numOptions,
      questions: parsedQuestions,
    },
  };
}
